import { Loader2 } from 'lucide-react';
import { Navigate, Outlet, useLocation } from 'react-router';
import type { Permission } from '@hrms/shared';
import { useAuth } from '@/features/auth/use-auth';
import { useTermsGate } from '@/features/legal/legal-api';
import { useLocationGate } from '@/features/location/use-location-gate';
import { LocationBlockedScreen } from '@/features/location/location-blocked-screen';

/** Full-screen spinner shown while the initial silent refresh is in flight. */
function BootstrapScreen() {
  return (
    <div className="flex h-dvh items-center justify-center" role="status" aria-live="polite">
      <Loader2 className="size-6 animate-spin text-muted-foreground" aria-hidden="true" />
      <span className="sr-only">Loading</span>
    </div>
  );
}

/**
 * Requires an authenticated session.
 *
 * The `isBootstrapping` check is what stops a redirect storm: on a hard reload
 * the app has no access token until `/auth/refresh` returns, so rendering a
 * decision before then would bounce every signed-in user to the login screen
 * and lose their destination.
 */
export function RequireAuth() {
  const { isAuthenticated, isBootstrapping, user } = useAuth();
  const location = useLocation();
  // Called unconditionally, before any early return: hooks cannot sit behind a
  // branch, and this component returns in several places below.
  const { blocked: blockedByTerms } = useTermsGate();
  // Only ever true inside the Android shell, and only on a definite permission
  // refusal — see `useLocationGate`.
  const { blocked: blockedByLocation, recheck: recheckLocation } = useLocationGate();

  if (isBootstrapping) return <BootstrapScreen />;

  if (!isAuthenticated) {
    // Remember where they were headed so login can return them there.
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // The API refuses everything else anyway (MustChangePasswordGuard); routing
  // here turns a wall of 403s into a single clear instruction.
  if (user?.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  // Location is a condition of using the app on a company phone (see the
  // terms). Shown in place of everything, not as a route redirect, so there is
  // nowhere to navigate around it — the app is simply not usable until location
  // is on. Never fires in a browser or on a mere `prompt`, so it cannot strand
  // office staff or someone who has not been asked yet.
  if (blockedByLocation) {
    return <LocationBlockedScreen onRecheck={recheckLocation} />;
  }

  // After the password check, not before: somebody on a temporary password
  // should finish that first, and the API refuses everything either way.
  // Same reasoning as above — the server is the control, this turns a wall of
  // 403s into one clear instruction.
  if (blockedByTerms && location.pathname !== '/terms') {
    return <Navigate to="/terms" replace />;
  }

  return <Outlet />;
}

/** Redirects an already-signed-in user away from login and similar pages. */
export function RequireAnonymous() {
  const { isAuthenticated, isBootstrapping } = useAuth();

  if (isBootstrapping) return <BootstrapScreen />;
  if (isAuthenticated) return <Navigate to="/" replace />;

  return <Outlet />;
}

interface RequirePermissionProps {
  /** Reachable when the user holds **any** of these. */
  anyOf: Permission[];
}

/**
 * Route-level authorisation.
 *
 * Convenience and clarity only — it prevents a confusing screen full of failed
 * requests. The API is the authority; nothing here is a security boundary,
 * since anyone can edit the bundle in their own browser.
 */
export function RequirePermission({ anyOf }: RequirePermissionProps) {
  const { canAny, isBootstrapping } = useAuth();

  if (isBootstrapping) return <BootstrapScreen />;
  if (!canAny(...anyOf)) return <Navigate to="/forbidden" replace />;

  return <Outlet />;
}
